<template>
  <div class="participant-section">
    <br>
    <div v-if="!isLoading" class="container">
      <div class="columns">
        <div class="column">
          <div class="box submission-item">
            <div class="columns">
              <div class="column">
                <div class="headline">
                  <div
                    v-if="division.isOwner"
                  >
                    <h1 class="is-size-4 has-text-weight-bold">{{ division.name }}</h1>
                  </div>
                  <div
                    v-else
                  >
                    <div class="d-flex align-center">
                      <h1 class="is-size-4 has-text-weight-bold">{{ division.userFirstName }} {{ division.userLastName }}</h1>
                    </div>
                    <h2 class="is-size-5">{{ division.name }}</h2>
                  </div>

                  <div class="d-flex">
                    <a
                      v-if="currentUser._id === division.userId"
                      href="#"
                      class="button is-dark is-outlined is-small"
                      @click.prevent="toggleModal"
                    ><span><AlertCircleOutline  class="has-text-danger"/></span>&nbsp;Report issue</a>
                    <router-link
                      v-if="userRole === 'participant'"
                      :to="{ name: 'participant-dashboard', query: { tab: dashboardTab } }"
                      class="button is-primary is-small"
                    >Back
                    </router-link>
                  </div>
                  <router-link
                    v-if="userRole === 'judge'"
                    :to="{ name: 'judge-dashboard' }"
                    class="button is-primary is-small"
                  >Back
                  </router-link>

                </div>
                <p>Submitted on
                  <span
                    class="tooltip-hint"
                    v-tooltip="formatDate(division.submitDate, 'DD MMM YYYY  @ HH:mm')"
                  >
                    {{ formatDate(division.submitDate, 'DD MMM YYYY') }}
                  </span>
                </p>
              </div>
            </div>
            <div
              v-if="userRole === 'judge'"
              class="columns"
            >
              <div class="column">
                <h2 class="is-size-5 has-text-weight-bold">Final Score</h2>
                <div v-if="division.scores.length > 0">
                  <div v-if="division.hasGraded">
                    <span class="tag is-warning is-large"><b class="is-size-4">{{ division.finalScore }}</b></span>
                  </div>
                  <div v-else>
                    Final score can only be seen once you've graded the submission.
                  </div>
                </div>
                <div v-else>This submission hasn't been graded yet</div>
              </div>
            </div>
            <div
              v-else
              class="columns"
            >
              <div class="column">
                <div v-if="division.scores.length > 0">
                  <h2 class="is-size-5 has-text-weight-bold">Final Score</h2>
                  <span class="tag is-warning is-large"><b class="is-size-4">{{ division.finalScore }}</b></span>
                  <div class="columns">
                    <div class="column">
                      <br>
                      <h3 class="is-size-5 has-text-weight-bold">Individual Rubrics</h3>
                      <p>Values are averaged from all judges' scores</p>
                    </div>
                  </div>
                  <table class="table score-table is-fullwidth is-striped">
                    <thead>
                      <tr>
                        <td>Rubric</td>
                        <td>Score</td>
                        <td>Comments/Observations</td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="rubric in rubricsScores"
                        :key="rubric.slug"
                      >
                        <th class="has-text-grey-dark">{{ rubric.name }}</th>
                        <td>{{ rubric.score }}</td>
                        <td>
                          <div v-if="rubric.comments.length > 0">
                            <p
                              v-for="(comment, index) in rubric.comments"
                              :key="index"
                            ><em>"{{ comment }}"</em></p>
                          </div>
                          <div v-else>
                            <span class="has-text-grey">-</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else>
                  <h2 class="is-size-5 has-text-weight-bold">Final Score</h2>
                  <p>This submission hasn't been graded yet</p>
                </div>
              </div>
            </div>

            <div class="columns">
              <div class="column">
                <h2
                  v-if="userRole === 'participant'"
                  class="is-size-5 has-text-weight-bold"
                >Video Submission</h2>
                <div
                  v-if="division.fileName"
                  class="video-player-wrapper"
                >
                  <video
                    v-bind="videoAttrs"
                    id="videoPlayer"
                    controls
                  >
                    <pre v-if="!division.isOwner">&lt;video controls controlsList="nodownload"&gt;</pre>
                    <source
                      :src="streamEndpoint"
                      type="video/mp4"
                    >
                  </video>
                </div>
                <div v-else-if="division.youtubeId">
                  <div class='embed-container'>
                    <iframe
                    :src='`https://www.youtube.com/embed/${division.youtubeId}`' frameborder='0' allowfullscreen></iframe>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="userRole === 'judge'"
            >
              <Grading
                v-if="!division.hasGraded"
                @saveRubrics="saveRubrics"
              />
              <div
                v-else
              >
                <div class="columns">
                  <div class="column">
                    <h2 class="is-size-5 has-text-weight-bold">Rubrics</h2>
                    <p class="has-text-grey">You've already graded this submission</p>
                  </div>
                </div>

                <div
                  v-for="rubric in rubrics"
                  :key="rubric.slug"
                  class="columns"
                >
                  <div class="column is-full">
                    <h2 class="is-size-6"><span class="has-text-weight-bold">{{ rubric.name }}</span>&nbsp;—&nbsp;<span>{{ rubric.score }}</span></h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <IssueModal
      :is-open="isOpenModal"
      :division-name="division.name"
      @closeModal="toggleModal"
    />
  </div>
</template>

<script>
import datesMixin from '@/mixins/dates'
import Grading from '@/components/submissions/Grading'
import IssueModal from '@/components/modals/IssueModal'
import AlertCircleOutline from 'vue-material-design-icons/AlertCircleOutline.vue'

export default {
  name: 'Division',
  mixins: [datesMixin],
  components: {
    Grading,
    IssueModal,
    AlertCircleOutline
  },
  metaInfo: {
    title: `Submit - ${process.env.VUE_APP_PAGE_TITLE}`
  },
  data () {
    return {
      isLoading: true,
      isOpenModal: false,
      //  Variables
      divisionId: '',
      userId: '',
      division: {
        name: 'Loading..',
        fileName: false,
        scores: []
      },
      rubricsScores: {},
      variation: 'user'
    }
  },
  mounted () {
    this.divisionId = this.$router.currentRoute.params.divisionId

    this.userId = this.$router.currentRoute.query.user

    this.getDivision()

    if (this.userRole === 'judge') {
      this.variation = 'judge'
    }
  },
  computed: {
    videoAttrs () {
      return this.division.isOwner ? {} : { controlsList: 'nodownload' }
    },
    streamEndpoint () {
      return `${process.env.VUE_APP_API}/divisions/stream/${this.division.fileName}`
    },
    userRole () {
      return this.$store.getters['users/role']
    },
    currentUser () {
      return this.$store.getters['users/currentUser']
    },
    gradeIndex () {
      return this.division.scores.findIndex(item => {
        return item.judge === this.currentUser._id
      })
    },
    rubrics () {
      return this.division.scores[this.gradeIndex].rubrics
    },
    dashboardTab () {
      return this.$store.getters.tab
    }
  },
  watch: {
    division () {
      this.division.scores.map(score => {
        score.rubrics.map(rubric => {
          if (rubric.slug in this.rubricsScores) {
            this.rubricsScores[rubric.slug]['scores'].push(rubric.score)
            this.rubricsScores[rubric.slug]['score'] = Math.round(this.rubricsScores[rubric.slug]['scores'].reduce((a, b) => { return a + b }) / this.rubricsScores[rubric.slug]['scores'].length * 100) / 100
          } else {
            this.rubricsScores[rubric.slug] = {
              name: rubric.name,
              score: rubric.score,
              scores: [rubric.score],
              comments: []
            }
          }

          if (rubric.comment.length > 0) {
            this.rubricsScores[rubric.slug]['comments'].push(rubric.comment)
          }
        })
      })
    }
  },
  methods: {
    getDivision () {
      this.isLoading = true
      let data = {
        divisionId: this.divisionId
      }

      if (typeof this.userId !== 'undefined') {
        data['userId'] = this.userId
      }

      this.$store
        .dispatch('users/GET_DIVISION', data)
        .then(res => {
          let data = res.data
          data.map(submission => {
            if (typeof submission.scores !== 'undefined') {
              if (submission.scores.length > 0) {
                //  Get total avg score
                let finalScore = []

                submission.scores.map(grading => {
                  finalScore.push(grading.score)
                })

                finalScore = Math.round(finalScore.reduce((a, b) => { return a + b }) / finalScore.length * 100) / 100
                submission['finalScore'] = finalScore

                //  Determine if current user is judge and has graded submission
                if (this.currentUser.role === 'judge') {
                  const hasGraded = submission.scores.findIndex(item => {
                    return item.judge === this.currentUser._id
                  }) >= 0
                  submission['hasGraded'] = hasGraded
                }

                //  Determine if current user is submission owner
                if (this.currentUser._id === submission.userId) {
                  submission['isOwner'] = true
                } else {
                  submission['isOwner'] = false
                }
              }
            }
          })
          this.division = data[0]
          this.isLoading = false
        })
        .catch(err => {
          this.$toasted.error('Error retrieving submission')
          console.log(err)
        })
    },
    saveRubrics (data) {
      this.$store
        .dispatch('submissions/SAVE_SUBMISSION_GRADE', {
          userId: this.userId,
          divisionId: this.divisionId,
          activeEvent: this.$store.getters.currentEvent._id,
          rubrics: data.rubrics,
          score: data.score
        })
        .then(res => {
          this.getDivision()
        })
    },
    toggleModal () {
      this.isOpenModal = !this.isOpenModal
    }
  }
}
</script>

<style lang="scss">
  .headline{
    display: flex;
    justify-content: space-between;

    .button{
      margin-left: 15px;
    }
  }
  .video-player-wrapper{
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
  }
  .embed-container {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 100%;
  }
  .embed-container iframe,
  .embed-container object,
  .embed-container embed {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
</style>
